// Entry point: load data, mount the app. Search-first single column;
// compact rows expand in place to show terminals and citations.

import m from "mithril";
import type { Airport, Terminal } from "./types";
import { electronicsLabel, flagEmoji, liquidsAmount } from "./labels";
import { readUrl, state, toggleExpanded, visibleAirports, writeUrl } from "./state";

// Decorative cloud layer behind the content; purely presentational.
function clouds() {
  const puffs = [1, 2, 3, 4, 5];
  return m("div.clouds", { "aria-hidden": "true" }, puffs.map((puff) => m(`span.cloud.c${puff}`)));
}

// A labelled source link; renders nothing when the url is absent.
function sourceLink(label: string, url: string | null | undefined) {
  if (!url) return null;
  return m("a.source", { href: url, target: "_blank", rel: "noopener" }, [
    label,
    m("span.arrow", " ↗"),
  ]);
}

// A labelled cell: small uppercase key over its raw value.
function pair(key: string, value: string) {
  return m("div.pair", [m("span.k", key), m("span.v", value)]);
}

// Put a term into the search box; the list filters to it.
function searchFor(term: string): void {
  state.query = term;
  writeUrl();
}

// Clear the search and collapse everything — back to the default view.
function resetState(): void {
  state.query = "";
  state.expanded.clear();
  writeUrl();
}

// Country cell whose value searches for that country on click.
function countryPair(country: string) {
  return m("div.pair", [
    m("span.k", "Country"),
    m("button.v.link", { onclick: () => searchFor(country) }, country),
  ]);
}

// Present fields only, so nothing is invented for missing data.
function pairs(airport: Airport) {
  const electronics = electronicsLabel(airport.coverage);
  const liquids = liquidsAmount(airport.liquids_limit_ml);
  const cells = [];
  if (electronics) cells.push(pair("Electronics", electronics));
  if (liquids) cells.push(pair("Liquids", liquids));
  if (airport.as_of) cells.push(pair("As of", airport.as_of));
  if (airport.country) cells.push(countryPair(airport.country));
  return m("div.pairs", cells);
}

const TerminalRow = {
  view({ attrs }: m.Vnode<{ terminal: Terminal }>) {
    const terminal = attrs.terminal;
    const coverage = terminal.coverage ?? "unknown";
    const liquids = liquidsAmount(terminal.liquids_limit_ml);
    return m("li.terminal", [
      m("span.term-name", terminal.name ?? ""),
      m("span.term-status", { class: `cov-${coverage}` }, coverage),
      liquids ? m("span.term-liquids", liquids) : null,
    ]);
  },
};

function detail(airport: Airport) {
  return m("div.detail", [
    pairs(airport),
    airport.terminals
      ? m("div.terminals", [
          m("h4", "Terminals"),
          m(
            "ul",
            airport.terminals.map((terminal) =>
              m(TerminalRow, { key: terminal.name, terminal }),
            ),
          ),
        ])
      : null,
    m("div.sources", [
      m("span.sources-label", "Sources"),
      sourceLink("electronics", airport.citation),
      sourceLink("liquids", airport.liquids_citation),
    ]),
  ]);
}

const Row = {
  view({ attrs }: m.Vnode<{ airport: Airport }>) {
    const airport = attrs.airport;
    const open = state.expanded.has(airport.iata);
    const flag = flagEmoji(airport.cc);
    return m(
      "div.row",
      { class: `cov-${airport.coverage}${open ? " open" : ""}` },
      [
        m(
          "button.row-head",
          {
            onclick: () => toggleExpanded(airport.iata),
            "aria-expanded": open,
          },
          [
            m("span.dot", { class: `cov-${airport.coverage}` }),
            m("span.headline", [
              m("span.title", [
                flag ? m("span.flag", flag) : null,
                m("span.iata", airport.iata),
                m("span.name", airport.name ?? ""),
              ]),
            ]),
            m("span.chev", open ? "▾" : "▸"),
          ],
        ),
        open ? detail(airport) : null,
      ],
    );
  },
};

// Colour key beneath the search box: dot + what each coverage state means.
const LEGEND: [string, string][] = [
  ["full", "electronics stay"],
  ["partial", "some lanes"],
  ["none", "electronics out"],
  ["unknown", "unknown"],
];

function legend() {
  return m(
    "div.legend",
    LEGEND.map(([coverage, text]) =>
      m("span.legend-item", [
        m("span.dot", { class: `cov-${coverage}` }),
        m("span.legend-text", text),
      ]),
    ),
  );
}

function searchBox() {
  return m("input.search[type=search]", {
    placeholder: "search airport, city, country…",
    value: state.query,
    oninput: (event: InputEvent) => {
      state.query = (event.target as HTMLInputElement).value;
      writeUrl();
    },
    oncreate: (vnode: m.VnodeDOM) => (vnode.dom as HTMLInputElement).focus(),
  });
}

const App = {
  view() {
    if (!state.loaded) return m("p.loading", "Loading…");
    const results = visibleAirports();
    const count = `${results.length} airport${results.length === 1 ? "" : "s"}`;
    // clouds() is a sibling of main so it stays behind all content.
    return [
      clouds(),
      m("main", [
        m("header", [
          m("h1", { onclick: resetState, title: "Reset" }, "Air"),
          searchBox(),
          legend(),
        ]),
        m("p.count", count),
        results.length
          ? m(
              "div.results",
              results.map((airport) => m(Row, { key: airport.iata, airport })),
            )
          : m("p.empty", "No matches."),
      ]),
    ];
  },
};

async function main() {
  readUrl();
  const root = document.getElementById("app")!;
  m.mount(root, App);
  // Bust the data cache with the build id baked into the page.
  const build = root.dataset.build ?? "";
  const response = await fetch(`data.json?${build}`);
  state.airports = await response.json();
  state.loaded = true;
  m.redraw();
}

main();
